/**
 * Singleton manager for global Firestore subscriptions.
 *
 * Problem: DashboardLayout is used as `component` in ~30 separate route groups.
 * When navigating between them (e.g. /projects → /tickets), Vue destroys the old
 * DashboardLayout instance and creates a new one. The old instance's onUnmounted
 * fires AFTER the new instance's onMounted, killing the freshly-created subscriptions.
 *
 * Solution: Reference counting. Each DashboardLayout mount increments the counter,
 * each unmount decrements it. Subscriptions are only created on the first mount
 * and only destroyed when the counter reaches 0 (i.e., actually leaving the dashboard).
 */
import { toast } from 'vue-sonner'
import { useNotificationsStore } from '@/stores/notifications'
import { useMeetingsStore } from '@/views/Meetings/store'
import { useProjectsStore } from '@/views/Projects/store'
import { useUsersStore } from '@/views/Users/store'
import { useOrganizationsStore } from '@/views/Organizations/store'
import { useApplicationsStore } from '@/views/Applications/store'
import { useDeliverablesStore } from '@/views/Deliverables/store'
import { useRequirementsStore } from '@/views/Requirements/store'
import { useEpicsStore } from '@/views/Epics/store'
import { useTicketsStore } from '@/views/Tickets/store'
import { usePetitionsStore } from '@/views/Petitions/store'
import { useReleasesStore } from '@/views/Releases/store'
import { useBacklogStore } from '@/views/Backlog/store'
import { useAppointmentsStore } from '@/views/Appointments/store'
import { useCRMStore } from '@/views/CRM/store'
import { useQuotationsStore } from '@/views/Quotations/store'
import { useSalesOrdersStore } from '@/views/SalesOrders/store'
import { useTasksStore } from '@/views/Tasks/store'
import { useTaskBoardsStore } from '@/views/Tasks/store/boards'
import { useImplementationsStore } from '@/views/Implementations/store'

let refCount = 0
let isSubscribed = false

async function subscribe() {
  if (isSubscribed) return
  isSubscribed = true

  const notificationsStore = useNotificationsStore()
  const projectsStore = useProjectsStore()
  const usersStore = useUsersStore()
  const organizationsStore = useOrganizationsStore()
  const meetingsStore = useMeetingsStore()
  const applicationsStore = useApplicationsStore()
  const requirementsStore = useRequirementsStore()
  const epicsStore = useEpicsStore()
  const ticketsStore = useTicketsStore()
  const petitionsStore = usePetitionsStore()
  const releasesStore = useReleasesStore()
  const backlogStore = useBacklogStore()
  const appointmentsStore = useAppointmentsStore()
  const crmStore = useCRMStore()
  const quotationsStore = useQuotationsStore()
  const salesOrdersStore = useSalesOrdersStore()
  const tasksStore = useTasksStore()
  const boardsStore = useTaskBoardsStore()
  const deliverablesStore = useDeliverablesStore()
  const implementationsStore = useImplementationsStore()

  notificationsStore.setOnNewNotification((title: string, message: string) => {
    toast(title, { description: message })
  })
  notificationsStore.subscribe()

  // Phase 1: Core
  projectsStore.subscribeGlobal()
  usersStore.subscribeGlobal()
  meetingsStore.subscribeGlobal()
  organizationsStore.subscribeGlobal()

  // Phase 2: Features
  applicationsStore.subscribeGlobal()
  requirementsStore.subscribeGlobal()
  epicsStore.subscribeGlobal()
  ticketsStore.subscribeGlobal()
  petitionsStore.subscribeGlobal()
  releasesStore.subscribeGlobal()
  deliverablesStore.subscribeGlobal()
  implementationsStore.subscribeGlobal()
  backlogStore.subscribeGlobal()

  // Phase 3: CRM & Sales
  appointmentsStore.subscribeGlobal()
  crmStore.subscribeGlobal()
  quotationsStore.subscribeGlobal()
  salesOrdersStore.subscribeGlobal()

  // Tasks: boards first, then tasks with visibility
  await boardsStore.fetchBoards()
  const userBoardIds = boardsStore.boards.map((b) => b.id)
  tasksStore.subscribeGlobal(userBoardIds)
}

function unsubscribe() {
  if (!isSubscribed) return
  isSubscribed = false

  const notificationsStore = useNotificationsStore()
  const projectsStore = useProjectsStore()
  const usersStore = useUsersStore()
  const organizationsStore = useOrganizationsStore()
  const meetingsStore = useMeetingsStore()
  const applicationsStore = useApplicationsStore()
  const requirementsStore = useRequirementsStore()
  const epicsStore = useEpicsStore()
  const ticketsStore = useTicketsStore()
  const petitionsStore = usePetitionsStore()
  const releasesStore = useReleasesStore()
  const backlogStore = useBacklogStore()
  const appointmentsStore = useAppointmentsStore()
  const crmStore = useCRMStore()
  const quotationsStore = useQuotationsStore()
  const salesOrdersStore = useSalesOrdersStore()
  const tasksStore = useTasksStore()
  const deliverablesStore = useDeliverablesStore()
  const implementationsStore = useImplementationsStore()

  notificationsStore.setOnNewNotification(null)
  notificationsStore.cleanup()
  projectsStore.unsubscribeGlobal()
  usersStore.unsubscribeGlobal()
  meetingsStore.unsubscribeGlobal()
  organizationsStore.unsubscribeGlobal()
  applicationsStore.unsubscribeGlobal()
  deliverablesStore.unsubscribeGlobal()
  implementationsStore.unsubscribeGlobal()
  requirementsStore.unsubscribeGlobal()
  epicsStore.unsubscribeGlobal()
  ticketsStore.unsubscribeGlobal()
  petitionsStore.unsubscribeGlobal()
  releasesStore.unsubscribeGlobal()
  backlogStore.unsubscribeGlobal()
  appointmentsStore.unsubscribeGlobal()
  crmStore.unsubscribeGlobal()
  quotationsStore.unsubscribeGlobal()
  salesOrdersStore.unsubscribeGlobal()
  tasksStore.unsubscribeGlobal()
}

export function useGlobalSubscriptions() {
  function onMount() {
    refCount++
    if (refCount === 1) {
      subscribe()
    }
  }

  function onUnmount() {
    refCount--
    // Use setTimeout to let the new instance mount first
    // This handles the case where old unmount fires before new mount
    setTimeout(() => {
      if (refCount <= 0) {
        refCount = 0
        unsubscribe()
      }
    }, 100)
  }

  return { onMount, onUnmount }
}
